// Rotate only the visible mobile product preview. Both image faces remain in
// the markup, so this controls presentation without changing catalog identity.
export function startCatalogRotation({
  element,
  onRotate,
  browser = typeof window === 'undefined' ? null : window,
}) {
  if (!element || typeof onRotate !== 'function' || !browser?.matchMedia) {
    return () => {};
  }

  const page = browser.document;
  const mobile = browser.matchMedia('(max-width: 768px)');
  const reducedMotion = browser.matchMedia('(prefers-reduced-motion: reduce)');
  let disposed = false;
  let timer = null;
  let observer = null;
  // Older browsers without IntersectionObserver keep the original mobile
  // front/back rotation, but still pause for hidden pages and reduced motion.
  let inView = typeof browser.IntersectionObserver !== 'function';

  const canRotate = () => !disposed
    && mobile.matches
    && !reducedMotion.matches
    && page?.hidden !== true
    && page?.visibilityState !== 'hidden'
    && inView;

  const clearTimer = () => {
    if (timer === null) return;
    browser.clearTimeout(timer);
    timer = null;
  };

  const schedule = (delay) => {
    timer = browser.setTimeout(() => {
      timer = null;
      if (!canRotate()) return;
      onRotate();
      if (canRotate()) schedule(3000);
    }, delay);
  };

  const refresh = () => {
    if (!canRotate()) {
      clearTimer();
    } else if (timer === null) {
      schedule(Math.floor(Math.random() * 3000));
    }
  };

  const listenToMedia = (media) => {
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', refresh);
      return () => media.removeEventListener('change', refresh);
    }
    if (typeof media.addListener === 'function') {
      media.addListener(refresh);
      return () => media.removeListener(refresh);
    }
    return () => {};
  };

  const removeMobileListener = listenToMedia(mobile);
  const removeMotionListener = listenToMedia(reducedMotion);
  page?.addEventListener?.('visibilitychange', refresh);

  if (typeof browser.IntersectionObserver === 'function') {
    observer = new browser.IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === element);
      if (!entry) return;
      inView = entry.isIntersecting;
      refresh();
    });
    observer.observe(element);
  }

  refresh();

  return () => {
    disposed = true;
    clearTimer();
    observer?.disconnect();
    removeMobileListener();
    removeMotionListener();
    page?.removeEventListener?.('visibilitychange', refresh);
  };
}
